import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { SubscriptionGate } from '@/components/paywall/SubscriptionGate';
import { localCards } from '@/content/mobile';
import { ArcadeGame } from '@/features/games/ArcadeGame';
import { MatchGame, SentenceGame, SpellGame, type SentencePuzzle } from '@/features/games/LearningGames';
import { YaYaGame } from '@/features/games/YaYaGame';
import { usePremiumAccess } from '@/hooks/usePremiumAccess';
import { track } from '@/lib/analytics';
import { learnerApiJson } from '@/lib/learner-api';
import { buildSentenceGateCopy, nabGateCopy, sentencePracticeGateCopy } from '@/lib/paywall';
import { useLearnerAuth } from '@/providers/LearnerAuth';
import { useLearnerPreferences } from '@/providers/LearnerPreferences';

export const Route = createFileRoute('/app/games/$slug')({ component: GameRoute });

function GameRoute() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const preferences = useLearnerPreferences();
  const auth = useLearnerAuth();
  const premium = usePremiumAccess();
  const [paywallVisible, setPaywallVisible] = useState(false);
  const puzzles = useQuery({
    queryKey: ['sentence-puzzles', slug],
    queryFn: async () => {
      const rows = await learnerApiJson<Array<SentencePuzzle & { prompt_translations?: Record<string, string> }>>(
        auth?.getToken ?? null,
        '/v1/sentence-puzzles',
      );
      return rows.map((row) => ({
        ...row,
        prompt: row.prompt_translations?.[preferences.baseLanguage] || row.prompt_translations?.en,
      }));
    },
    enabled: slug === 'build-sentence' || slug === 'practice',
  });

  if (slug === 'nab' && premium.isReady && !premium.isSubscribed) {
    const gate = nabGateCopy();
    return (
      <SubscriptionGate
        source={gate.source}
        content="nab"
        message={gate.message}
        heroEmoji={gate.heroEmoji}
        onDismiss={() => void navigate({ to: '/app/games' })}
      />
    );
  }

  if (slug === 'build-sentence' || slug === 'practice') {
    const gate = slug === 'practice' ? sentencePracticeGateCopy() : buildSentenceGateCopy();
    const showJoinPremium = premium.isReady && !premium.isSubscribed;
    return puzzles.data ? (
      <>
        <SentenceGame
          puzzles={puzzles.data}
          slug={slug}
          onClose={() => void navigate({ to: '/app/games' })}
          showJoinPremiumCTA={showJoinPremium}
          onJoinPremiumPress={() => {
            track(
              slug === 'practice' ? 'Sentence Practice Join Premium Press' : 'Build Sentence Join Premium Press',
              { game: slug },
            );
            setPaywallVisible(true);
          }}
        />
        {showJoinPremium ? (
          <SubscriptionGate
            source={gate.source}
            content={slug}
            message={gate.message}
            heroEmoji={gate.heroEmoji}
            variant="modal"
            open={paywallVisible}
            onDismiss={() => setPaywallVisible(false)}
          />
        ) : null}
      </>
    ) : (
      <Loading />
    );
  }

  if (slug === 'ya-ya') return <YaYaGame onClose={() => void navigate({ to: '/app/games' })} />;
  if (slug === 'nab') return <ArcadeGame kind="nab" />;
  const close = () => void navigate({ to: '/app/games' });
  if (slug === 'match-numbers') {
    return <MatchGame title="Guess the Number" slug={slug} cards={localCards('numbers', preferences.dialect, preferences.baseLanguage)} onClose={close} />;
  }
  if (slug === 'spell-numbers') {
    const cards = localCards('numbers', preferences.dialect, preferences.baseLanguage).map((card) => ({
      ...card,
      title: card.translation,
      translation: card.title,
    }));
    return (
      <SpellGame
        title="Spell the Numbers"
        slug={slug}
        cards={cards}
        onClose={() => void navigate({ to: '/app/games' })}
      />
    );
  }
  if (slug === 'spell-family') {
    return (
      <SpellGame
        title="Spell the Family"
        slug={slug}
        cards={localCards('family', preferences.dialect, preferences.baseLanguage)}
        onClose={() => void navigate({ to: '/app/games' })}
      />
    );
  }
  if (slug === 'animal-guesser') {
    return <MatchGame title="What's the Animal?" slug={slug} cards={localCards('animals', preferences.dialect, preferences.baseLanguage)} onClose={close} />;
  }
  if (slug === 'body-parts') {
    return <MatchGame title="Match Body Parts" slug={slug} cards={localCards('body-parts', preferences.dialect, preferences.baseLanguage)} onClose={close} />;
  }
  return <MatchGame title="Guess the Family Member" slug="match-family" cards={localCards('family', preferences.dialect, preferences.baseLanguage)} onClose={close} />;
}

function Loading() {
  return (
    <main className="grid min-h-[70vh] place-items-center">
      <p>Loading game…</p>
    </main>
  );
}
